import { Pencil } from 'lucide-react'
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '~/components/common/dropdowns/DropdownMenu'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import {
  SequentialSelect,
  SequentialSelectStep,
  SequentialSelectStepItem
} from '~/components/common/SequentialSelect'
import { Typography } from '~/components/common/Typography'
import { UploadFile } from '~/components/common/UploadFile'
import { extractFileDisplayName } from '~/utils/repositoryUtils'
import { CommandType } from '~/constants/commands'
import { XmlFileUploadLeftIcon } from '~/constants/icon'
import { useIndexes } from '~/hooks'
import { useFolderPicker } from '~/hooks/useFolderPicker'
import { RepositoryBranch } from '~/hooks/useCodeContext'
import { uploadFile } from '~/services/organizationService'
import { projectService } from '~/services/plugin/projectService'
import { usePluginWorkspaceSafe } from '~/contexts/PluginWorkspaceContext'
import { bridge } from '~/services/bridge'
import { BetterToast } from '~/utils/toast'
import { UploadedReposList } from './UploadedReposList'
import { UploadedRepo, formatFileSize } from './types'

interface RepoSelectModalProps {
  // Visibility
  isOpen: boolean
  onClose: () => void

  // Header
  title: string

  // Repo source
  commandType: CommandType
  workspaceUuid: string

  // Initial state — snapshot taken on the closed→open transition.
  initialRepos?: RepositoryBranch[]
  initialUploads?: UploadedRepo[]

  // Confirm
  onConfirm: (repos: RepositoryBranch[], uploads: UploadedRepo[]) => void | Promise<void>

  // Behavior toggles
  /** When true, batch-upload tarballs to S3 before calling `onConfirm`. */
  uploadToS3OnConfirm?: boolean
  /** Hide the in-modal "Repositories Selected" list (default true). */
  showSelectedReposList?: boolean

  // Layout
  className?: string
  /** Forwarded to the underlying Modal overlay (used by the chatbox plugin layout). */
  overlayClassName?: string
  /** Tighter paddings for plugin / compact-viewport hosts. */
  compact?: boolean

  /**
   * Render a third "IDE Workspace" tab that surfaces projects from the
   * Cursor plugin context. Only takes effect when `bridge.isInPlugin()`.
   */
  showIdeTab?: boolean

  /**
   * Live-bind mode: every internal state change (repo add/remove/branch
   * edit, upload add/remove) fires `onConfirm` immediately so callers stay
   * in sync without a Confirm step. Used by the chatbox where the modal
   * acts as a live editor on top of parent state. Cancel becomes a plain
   * close (no revert) since changes are already committed upstream.
   */
  liveBind?: boolean
}

type TabKey = 'select' | 'upload' | 'ide'

export const RepoSelectModal: React.FC<RepoSelectModalProps> = ({
  isOpen,
  onClose,
  title,
  commandType,
  workspaceUuid,
  initialRepos = [],
  initialUploads = [],
  onConfirm,
  uploadToS3OnConfirm = false,
  showSelectedReposList = true,
  className = 'w-[640px] h-[629px]',
  overlayClassName,
  compact = false,
  showIdeTab = false,
  liveBind = false
}) => {
  const isPlugin = bridge.isInPlugin()
  const ideTabAvailable = showIdeTab && isPlugin
  const pluginWs = usePluginWorkspaceSafe()

  // Internal state
  const [activeTab, setActiveTab] = useState<TabKey>('select')
  const [selectedRepos, setSelectedRepos] = useState<RepositoryBranch[]>(initialRepos)
  const [uploadedRepos, setUploadedRepos] = useState<UploadedRepo[]>(initialUploads)
  const [selectedIdePaths, setSelectedIdePaths] = useState<Set<string>>(new Set())
  const [isLoading, setIsLoading] = useState(false)
  const [isWorkspaceUploading, setIsWorkspaceUploading] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)

  // Sync only on the closed→open transition so callers can pass freshly
  // allocated arrays without thrashing in-modal edits. Initialise to `false`
  // so mounting with `isOpen=true` still triggers the sync on first render.
  const prevIsOpenRef = useRef(false)
  // Snapshot of the parent's repos/uploads at the moment the modal opened,
  // used by Cancel in `liveBind` mode to undo all the in-session syncs and
  // restore the pre-open state upstream.
  const openSnapshotRef = useRef<{ repos: RepositoryBranch[]; uploads: UploadedRepo[] }>({
    repos: [],
    uploads: []
  })
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      openSnapshotRef.current = { repos: initialRepos, uploads: initialUploads }
      setSelectedRepos(initialRepos)
      setUploadedRepos(initialUploads)
      setSelectedIdePaths(new Set())
      if (initialRepos.length === 0 && initialUploads.length > 0) {
        setActiveTab('upload')
      } else {
        setActiveTab('select')
      }
    }
    prevIsOpenRef.current = isOpen
    // initialRepos / initialUploads intentionally omitted so we don't reset
    // mid-edit when the parent re-renders with a new array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Live-bind: keep parent in sync with internal state on every change.
  // Captured ref avoids re-firing the effect when the parent passes a new
  // function identity each render.
  const onConfirmRef = useRef(onConfirm)
  useEffect(() => {
    onConfirmRef.current = onConfirm
  }, [onConfirm])
  useEffect(() => {
    if (!liveBind || !isOpen) return
    void onConfirmRef.current(selectedRepos, uploadedRepos)
  }, [liveBind, isOpen, selectedRepos, uploadedRepos])

  // Repo source. `useIndexes` returns a freshly-filtered array on every call,
  // so we derive a stable signature from its contents and memoize derivatives
  // off of that. Without this, every modal render produces a new `repos`
  // array and a new `repositoryStep` object, which in turn causes
  // `SequentialSelect`'s reset effect to fire and dump any in-progress
  // repo→branch selection back to the repo step.
  const indexes = useIndexes({
    exculdeInternalRepos: true,
    commandType
  })
  const indexesSignature = useMemo(
    () => indexes.map(i => `${i.conn.conn_type}/${i.name.name}/${i.name.branch}`).join('|'),
    [indexes]
  )

  const repos = useMemo(
    () =>
      indexes
        .map(index => ({
          conn_type: index.conn.conn_type,
          index_name: index.name.name,
          label: extractFileDisplayName(`${index.conn.conn_type}:${index.name.name}`)
        }))
        .filter(
          (repo, idx, self) =>
            idx ===
            self.findIndex(t => t.conn_type === repo.conn_type && t.index_name === repo.index_name)
        ),
    // indexes ref churns each call; gate on its stable signature instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexesSignature]
  )

  const getBranches = useCallback(
    (conn_type: string, index_name: string) =>
      indexes
        .filter(index => index.conn.conn_type === conn_type && index.name.name === index_name)
        .map(index => index.name.branch),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indexesSignature]
  )

  // Stable repo→branch step graph for SequentialSelect. Memoized so picking a
  // repo (which only mutates SequentialSelect's internal `currStep`) does not
  // get clobbered by an unrelated parent re-render handing back a freshly
  // allocated step object.
  const repositoryStep = useMemo<SequentialSelectStep>(() => {
    const step: SequentialSelectStep = {
      label: 'Repository',
      itemsLabel: 'Select Repository',
      items: []
    }
    step.items = repos
      .filter(
        repo =>
          !selectedRepos.some(
            selected =>
              `${selected.conn_type}:${selected.repository}` ===
              `${repo.conn_type}:${repo.index_name}`
          )
      )
      .map(repo => {
        const branches = getBranches(repo.conn_type, repo.index_name)
        const branchStep: SequentialSelectStep = {
          label: 'Branch',
          itemsLabel: 'Select Branch',
          prev: step,
          items: branches.map(branch => ({ value: branch, label: branch }))
        }
        return {
          value: `${repo.conn_type}:${repo.index_name}`,
          label: repo.label,
          next: branchStep
        }
      })
    return step
  }, [repos, selectedRepos, getBranches])

  // Upload helpers --------------------------------------------------------
  const { pickFolder } = useFolderPicker()

  const handleFolderUpload = async () => {
    if (isLoading || isWorkspaceUploading) return
    setIsLoading(true)
    try {
      const result = await pickFolder('Select a directory to upload')
      if (!result) return
      const { tarFile, folderName, originalFilename } = result
      const newRepo: UploadedRepo = {
        id: Date.now(),
        name: folderName,
        size: formatFileSize(tarFile.size),
        originalFilename,
        tarFile,
        uploadStatus: 'pending',
        uploadType: 'directory',
        source: 'external'
      }
      setUploadedRepos(prev => [...prev, newRepo])
      BetterToast.success(`Directory "${folderName}" prepared for upload`)
    } catch (error) {
      BetterToast.error(
        `Failed to process folder: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveUpload = (id: number) => {
    setUploadedRepos(prev => prev.filter(r => r.id !== id))
  }

  // Selection helpers -----------------------------------------------------
  const handleSequentialComplete = (selections: SequentialSelectStepItem[]) => {
    const repoSelection = selections[0]
    const branchSelection = selections[1]
    const [conn_type, repository] = repoSelection.value.split(':')
    setSelectedRepos(prev => [...prev, { conn_type, repository, branch: branchSelection.value }])
  }

  const handleRemoveRepo = (repository: string, branch: string) => {
    setSelectedRepos(prev =>
      prev.filter(r => !(r.repository === repository && r.branch === branch))
    )
  }

  const handleChangeBranch = (index: number, newBranch: string) => {
    setSelectedRepos(prev => {
      const current = prev[index]
      if (!current || current.branch === newBranch) return prev
      return prev.map((r, i) => (i === index ? { ...r, branch: newBranch } : r))
    })
  }

  // IDE workspace upload --------------------------------------------------
  const toggleIdeProject = (path: string) => {
    setSelectedIdePaths(prev => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const handleUploadSelectedProjects = async () => {
    if (!ideTabAvailable || isLoading || isWorkspaceUploading) return
    const projects = pluginWs?.projects ?? []
    const toUpload = projects.filter(p => selectedIdePaths.has(p.path))
    if (toUpload.length === 0) return

    setIsWorkspaceUploading(true)
    try {
      for (const project of toUpload) {
        const alreadyUploaded = uploadedRepos.find(
          r => r.uploadType === 'directory' && r.name === project.name
        )
        if (alreadyUploaded) continue

        const uploadResult = await projectService.uploadProject(
          project.path,
          project.name || 'workspace',
          workspaceUuid
        )
        if (!uploadResult.success || !uploadResult.s3Path) {
          BetterToast.error(uploadResult.error || `Failed to upload ${project.name}`)
          continue
        }

        setUploadedRepos(prev => {
          if (prev.some(r => r.s3Path === uploadResult.s3Path)) return prev
          return [
            ...prev,
            {
              id: Date.now() + Math.random(),
              name: project.name || 'Workspace',
              size: uploadResult.size ? formatFileSize(uploadResult.size) : '-',
              originalFilename: `${project.name || 'workspace'}.tar.gz`,
              uploadStatus: 'completed',
              s3Path: uploadResult.s3Path,
              uploadType: 'directory',
              source: 'ide',
              ideProjectPath: project.path
            }
          ]
        })
      }
    } catch (error) {
      BetterToast.error(
        `Failed to upload workspace projects: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
    } finally {
      setIsWorkspaceUploading(false)
    }
  }

  // Confirm / Cancel ------------------------------------------------------
  const uploadFilesToS3 = async (uploads: UploadedRepo[]): Promise<UploadedRepo[]> => {
    if (uploads.length === 0) return uploads
    const uploadPromises = uploads.map(async repo => {
      if (repo.s3Path) return repo.s3Path
      if (!repo.tarFile) throw new Error(`No tar file found for ${repo.name}`)
      return new Promise<string>((resolve, reject) => {
        uploadFile(workspaceUuid, repo.tarFile!, {
          onComplete: (uploadedS3Path: string) => resolve(uploadedS3Path),
          onError: (error: string) => reject(new Error(error))
        })
      })
    })
    const s3Paths = await Promise.all(uploadPromises)
    return uploads.map((repo, idx) => ({ ...repo, s3Path: s3Paths[idx] }))
  }

  const handleCancel = () => {
    if (liveBind) {
      // Roll back every live-sync that happened while the modal was open
      // by pushing the open-time snapshot back to the parent. Without this
      // Cancel would silently keep all of the in-session edits because
      // liveBind has been propagating them upstream as the user picked.
      void onConfirmRef.current(openSnapshotRef.current.repos, openSnapshotRef.current.uploads)
    } else {
      setSelectedRepos(initialRepos)
      setUploadedRepos(initialUploads)
      setSelectedIdePaths(new Set())
    }
    onClose()
  }

  const handleConfirm = async () => {
    setIsConfirming(true)
    try {
      let finalUploads = uploadedRepos
      if (uploadToS3OnConfirm && uploadedRepos.length > 0) {
        try {
          finalUploads = await uploadFilesToS3(uploadedRepos)
        } catch (error) {
          console.error('Failed to upload files:', error)
          BetterToast.error('Failed to upload files to S3')
          return
        }
      }
      await onConfirm(selectedRepos, finalUploads)
      onClose()
    } catch (error) {
      console.error('Error in handleConfirm:', error)
      BetterToast.error('Failed to confirm selection')
    } finally {
      setIsConfirming(false)
    }
  }

  // Layout helpers --------------------------------------------------------
  const headerPadding = compact ? 'px-4 py-3' : 'px-6 py-4'
  const tabsPadding = compact ? 'pt-4 px-4' : 'pt-6 px-6'
  const tabButtonPadding = compact ? 'px-4 py-2' : 'px-6 py-2'
  const bodyPadding = compact ? 'px-4' : 'px-[60px]'

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      hideCloseButton={false}
      className={className}
      overlayClassName={overlayClassName}
    >
      <div className='h-full flex flex-col'>
        {/* Header */}
        <div
          className={`flex items-center justify-between border-b border-tokens-neutral-border-2 ${headerPadding}`}
        >
          <Typography
            fontFamily='geist'
            fontSize='titleSm'
            fontWeight='bold'
            className='text-tokens-neutral-text-primary'
          >
            {title}
          </Typography>
        </div>

        {/* Tabs */}
        <div className={`flex justify-center ${tabsPadding}`}>
          <div className='bg-tokens-neutral-bg-grey-2 p-1 rounded-xl flex'>
            <button
              onClick={() => setActiveTab('select')}
              className={`${tabButtonPadding} rounded-lg transition-all select-none ${
                activeTab === 'select'
                  ? 'bg-white shadow-segment text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-quaternary'
              }`}
              data-testid='tab-select-repo-button'
            >
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                Select Repo
              </Typography>
            </button>
            <button
              onClick={() => setActiveTab('upload')}
              className={`${tabButtonPadding} rounded-lg transition-all select-none ${
                activeTab === 'upload'
                  ? 'bg-white shadow-segment text-tokens-neutral-text-primary'
                  : 'text-tokens-neutral-text-quaternary'
              }`}
              data-testid='tab-upload-repos-button'
            >
              <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                Upload Repos
              </Typography>
            </button>
            {ideTabAvailable && (
              <button
                onClick={() => setActiveTab('ide')}
                className={`${tabButtonPadding} rounded-lg transition-all select-none ${
                  activeTab === 'ide'
                    ? 'bg-white shadow-segment text-tokens-neutral-text-primary'
                    : 'text-tokens-neutral-text-quaternary'
                }`}
                data-testid='tab-ide-workspace-button'
              >
                <Typography fontFamily='geist' fontSize='bodyMd' fontWeight='medium'>
                  IDE Workspace
                </Typography>
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <ModalBody className={`${bodyPadding} flex-1 overflow-y-auto`}>
          {activeTab === 'select' && (
            <>
              <SequentialSelect
                step={repositoryStep}
                onComplete={handleSequentialComplete}
                placeholder='Search or select...'
              />

              {showSelectedReposList && selectedRepos.length > 0 && (
                <div className='mt-8'>
                  <div className='px-6 mb-4'>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      fontWeight='semibold'
                      className='text-tokens-neutral-text-secondary'
                    >
                      Repositories Selected
                    </Typography>
                  </div>
                  <div className='bg-[#F9F9F9] rounded-3xl px-6 py-4'>
                    {selectedRepos.map((repoBranch, index) => {
                      const branchOptions = getBranches(repoBranch.conn_type, repoBranch.repository)
                      const repoDisplayName = extractFileDisplayName(repoBranch.repository)
                      return (
                        <Fragment key={`${repoBranch.repository}-${index}`}>
                          {index > 0 && (
                            <div className='w-full h-[1px] bg-tokens-neutral-border-2 my-2' />
                          )}
                          <div className='group flex items-center justify-between py-2'>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodyMd'
                              fontWeight='medium'
                              className='text-tokens-neutral-text-primary truncate'
                            >
                              {repoDisplayName} - {repoBranch.branch}
                            </Typography>
                            <div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity'>
                              {branchOptions.length > 0 && (
                                <DropdownMenu modal={false}>
                                  <DropdownMenuTrigger asChild>
                                    <button
                                      type='button'
                                      data-testid={`edit-branch-${index}`}
                                      aria-label={`Edit branch for ${repoDisplayName}`}
                                      className='p-1 rounded text-tokens-neutral-icon-default hover:bg-gray-200 hover:text-tokens-neutral-icon-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#846fe1] data-[state=open]:bg-gray-200'
                                    >
                                      <Pencil className='h-4 w-4' strokeWidth={1.8} aria-hidden />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent
                                    align='end'
                                    sideOffset={4}
                                    className='min-w-[200px] max-h-64 overflow-y-auto'
                                  >
                                    {branchOptions.map(branch => (
                                      <DropdownMenuItem
                                        key={branch}
                                        data-testid={`edit-branch-option-${index}-${branch}`}
                                        onSelect={() => handleChangeBranch(index, branch)}
                                      >
                                        <span className='truncate'>{branch}</span>
                                        {branch === repoBranch.branch && (
                                          <svg
                                            width='14'
                                            height='14'
                                            viewBox='0 0 14 14'
                                            fill='none'
                                            aria-hidden
                                            className='ml-2 text-tokens-neutral-icon-default'
                                          >
                                            <path
                                              d='M2.5 7.5L5.5 10.5L11.5 3.5'
                                              stroke='currentColor'
                                              strokeWidth='1.5'
                                              strokeLinecap='round'
                                              strokeLinejoin='round'
                                            />
                                          </svg>
                                        )}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                              <button
                                onClick={() =>
                                  handleRemoveRepo(repoBranch.repository, repoBranch.branch)
                                }
                                className='p-1 rounded hover:bg-gray-200'
                                aria-label={`Remove ${repoDisplayName}`}
                              >
                                <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
                                  <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M5.18306 5.18306C5.42714 4.93898 5.82286 4.93898 6.06694 5.18306L14.8169 13.9331C15.061 14.1771 15.061 14.5729 14.8169 14.8169C14.5729 15.061 14.1771 15.061 13.9331 14.8169L5.18306 6.06694C4.93898 5.82286 4.93898 5.42714 5.18306 5.18306Z'
                                    fill='#21272A'
                                  />
                                  <path
                                    fillRule='evenodd'
                                    clipRule='evenodd'
                                    d='M14.8169 5.18306C15.061 5.42714 15.061 5.82286 14.8169 6.06694L6.06694 14.8169C5.82286 15.061 5.42714 15.061 5.18306 14.8169C4.93898 14.5729 4.93898 14.1771 5.18306 13.9331L13.9331 5.18306C14.1771 4.93898 14.5729 4.93898 14.8169 5.18306Z'
                                    fill='#21272A'
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'upload' && (
            <div className='py-4'>
              <div onClick={handleFolderUpload} role='button'>
                <UploadFile
                  className='w-full'
                  variant='with-toggle'
                  uploadState={{
                    status: 'default',
                    progress: 0,
                    error: undefined,
                    file: { name: 'test', size: 1000 }
                  }}
                  defaultIcon={<XmlFileUploadLeftIcon />}
                  mainTitle='Upload Directory'
                  description={
                    isLoading
                      ? 'Analyzing and compressing project...'
                      : 'Select a directory to upload'
                  }
                  disabled={isLoading || isWorkspaceUploading}
                />
              </div>
              <UploadedReposList uploadedRepos={uploadedRepos} onRemove={handleRemoveUpload} />
            </div>
          )}

          {activeTab === 'ide' && ideTabAvailable && (
            <div className='py-4'>
              <div className='rounded-xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 p-3'>
                <div className='flex flex-col gap-1'>
                  {!pluginWs?.projects || pluginWs.projects.length === 0 ? (
                    <Typography
                      fontFamily='geist'
                      fontSize='bodySm'
                      fontWeight='normal'
                      className='text-tokens-neutral-text-tertiary'
                    >
                      No projects found in the IDE workspace.
                    </Typography>
                  ) : (
                    <>
                      {pluginWs.projects.map(project => {
                        const uploaded = uploadedRepos.find(
                          r => r.uploadType === 'directory' && r.name === project.name
                        )
                        const isSelected = selectedIdePaths.has(project.path)
                        return (
                          <button
                            key={project.path}
                            type='button'
                            onClick={() => {
                              if (uploaded) {
                                setUploadedRepos(prev => prev.filter(r => r.id !== uploaded.id))
                                setSelectedIdePaths(prev => {
                                  const next = new Set(prev)
                                  next.delete(project.path)
                                  return next
                                })
                              } else {
                                toggleIdeProject(project.path)
                              }
                            }}
                            disabled={isWorkspaceUploading}
                            className={`flex items-center gap-2.5 w-full rounded-lg px-2 py-2 text-left transition-colors ${
                              uploaded
                                ? 'bg-primary-50 hover:bg-default-100'
                                : isSelected
                                  ? 'bg-primary-50 hover:bg-primary-100'
                                  : 'hover:bg-tokens-neutral-bg-grey-2'
                            }`}
                          >
                            {uploaded ? (
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                viewBox='0 0 20 20'
                                fill='currentColor'
                                className='w-4 h-4 flex-shrink-0 text-success'
                              >
                                <path
                                  fillRule='evenodd'
                                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                                  clipRule='evenodd'
                                />
                              </svg>
                            ) : (
                              <div
                                className={`w-4 h-4 flex-shrink-0 rounded border-2 flex items-center justify-center transition-colors ${
                                  isSelected
                                    ? 'bg-primary border-primary'
                                    : 'border-tokens-neutral-border-2'
                                }`}
                              >
                                {isSelected && (
                                  <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 16 16'
                                    fill='white'
                                    className='w-3 h-3'
                                  >
                                    <path
                                      fillRule='evenodd'
                                      d='M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z'
                                      clipRule='evenodd'
                                    />
                                  </svg>
                                )}
                              </div>
                            )}
                            <div className='flex flex-col items-start min-w-0 flex-1'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary truncate w-full'
                              >
                                {project.name || 'Workspace'}
                              </Typography>
                              <Typography
                                fontFamily='geistMono'
                                fontSize='labelSm'
                                fontWeight='normal'
                                className='text-tokens-neutral-text-tertiary truncate w-full'
                              >
                                {project.path}
                              </Typography>
                            </div>
                          </button>
                        )
                      })}
                    </>
                  )}
                </div>
              </div>
              {!isWorkspaceUploading && selectedIdePaths.size > 0 && (
                <div className='mt-4 flex justify-end'>
                  <Button
                    variant='outlined'
                    onClick={handleUploadSelectedProjects}
                    isDisabled={isLoading}
                    className='w-full'
                  >
                    Upload{' '}
                    {pluginWs?.projects && selectedIdePaths.size === pluginWs.projects.length
                      ? 'All'
                      : selectedIdePaths.size}{' '}
                    Project{selectedIdePaths.size !== 1 ? 's' : ''}
                  </Button>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <Button variant='outlined' onClick={handleCancel} isDisabled={isConfirming}>
            Cancel
          </Button>
          <Button variant='primary' isDisabled={isConfirming} onClick={handleConfirm}>
            {isConfirming ? 'Confirming...' : 'Confirm'}
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  )
}

export default RepoSelectModal
